import { hasLowerLevel, levelAt, worldToRadar, type MapId } from "@69ly/maps";
import {
  filterNotes,
  nameKey,
  noteFilterSet,
  type CreateStratInput,
  type DemoComment,
  type DemoScope,
  type DemoDetail,
  type NoteFilter,
  type NoteSide,
  type Replay,
  type Scope,
  type Side,
  type StratItem,
} from "@69ly/shared";
import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Avatar } from "../components/Avatar.tsx";
import { Icon } from "../components/Icon.tsx";
import { Select } from "../components/Select.tsx";
import { roundsOf, type HeatFilter } from "../demo/heatmap.ts";
import type { GrenadeMatch } from "../demo/matching.ts";
import { grenadesAt, playersAt, type RoundIndex } from "../demo/scene.ts";
import { useAddComment, useComments, useDeleteComment, useEditComment } from "../lib/demo-queries.ts";
import { errorText } from "../lib/errors.ts";
import { useCreateStrat } from "../lib/strat-queries.ts";
import { LastsField, linkInputs, LinksEditor, NO_ABOUT, noteWhen, NotePlayers, NoteSideTeam, NoteTags, PlayerToggles, type NoteAbout } from "./NoteFields.tsx";

const clock = (s: number) => {
  const v = Math.max(0, Math.ceil(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

// ---------------------------------------------------------------- heatmap

/** The Heatmap tab: what to show on the map instead of the round. */
export function HeatmapPanel({ replay, round, filter, onChange, samples }: { replay: Replay; round: RoundIndex; filter: HeatFilter; onChange: (f: HeatFilter) => void; samples: number }) {
  const { t } = useTranslation();
  const set = (change: Partial<HeatFilter>) => onChange({ ...filter, ...change });
  const rounds = roundsOf(replay, filter, round.index);
  const seg = <K extends keyof HeatFilter>(key: K, values: readonly HeatFilter[K][], label: string) => (
    <div className="field">
      <span id={`heat-${key}`}>{label}</span>
      <div className="segmented segmented-small" role="group" aria-labelledby={`heat-${key}`}>
        {values.map((v) => (
          <button key={String(v)} type="button" className="seg" aria-pressed={filter[key] === v} onClick={() => set({ [key]: v } as Partial<HeatFilter>)}>
            {t(`demos.heat.${key}.${String(v)}`, { n: round.round.number })}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="demo-heat">
      {seg("what", ["presence", "deaths", "kills"] as const, t("demos.heat.whatLabel"))}
      {seg("rounds", ["this", "all", "first", "second"] as const, t("demos.heat.roundsLabel"))}
      {seg("side", ["both", "t", "ct"] as const, t("demos.heat.sideLabel"))}
      <div className="field">
        <span>{t("demos.heat.playerLabel")}</span>
        <Select
          label={t("demos.heat.playerLabel")}
          value={filter.player === null ? "" : String(filter.player)}
          placeholder={t("demos.heat.everyone")}
          groups={[{ options: [{ value: "", label: t("demos.heat.everyone") }, ...replay.players.map((p, i) => ({ value: String(i), label: p.name }))] }]}
          onChange={(v) => set({ player: v === "" ? null : Number(v) })}
        />
      </div>
      <p className="muted small" role="status">
        {samples === 0 ? t("demos.heat.empty") : t(`demos.heat.legend.${filter.what}`, { count: rounds.length })}
      </p>
      <div className="demo-heat-scale" aria-hidden="true">
        <span>{t("demos.heat.less")}</span>
        <span className="demo-heat-ramp" />
        <span>{t("demos.heat.more")}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- a moment as a strat board

/** A strat board of this moment: both teams where they stand and look, the smokes and fires up, linked to stuffs when they are. */
export function boardFromMoment(o: {
  replay: Replay;
  map: MapId;
  round: RoundIndex;
  tick: number;
  numbers: number[];
  matches: GrenadeMatch[];
  side: Side;
  title: string;
  phase: string;
  notes: string;
  scope: Scope;
}): CreateStratInput {
  const { replay, map, round, tick, numbers } = o;
  const levels = hasLowerLevel(map);
  const lvl = (z: number) => (levels ? levelAt(map, z) : "upper");
  const items: StratItem[] = [];
  for (const p of playersAt(replay, round, tick)) {
    if (!p.alive) continue;
    const at = worldToRadar(map, p.x, p.y);
    const rad = (p.yaw * Math.PI) / 180;
    const look = worldToRadar(map, p.x + Math.cos(rad) * 450, p.y + Math.sin(rad) * 450);
    items.push({ id: `p${p.index}`, kind: "token", level: lvl(p.z), side: p.side, n: numbers[p.index] ?? 1, u: at.u, v: at.v, look: [look.u, look.v], view: p.side === o.side });
  }
  const stuffOf = new Map(o.matches.filter((m) => m.stuff).map((m) => [m.grenade, m.stuff!.id]));
  grenadesAt(replay, round, tick).forEach((g, i) => {
    if (g.state !== "effect" || (g.kind !== "smoke" && g.kind !== "molotov")) return;
    const at = worldToRadar(map, g.x, g.y);
    items.push({ id: `g${i}`, kind: "grenade", level: lvl(g.z), grenade: g.kind, u: at.u, v: at.v, stuffId: stuffOf.get(g.grenade) ?? null });
  });
  return {
    scope: o.scope,
    map,
    side: o.side,
    title: o.title.slice(0, 60),
    notes: o.notes.slice(0, 1000),
    phases: [{ id: "p1", name: o.phase.slice(0, 30), items: items.slice(0, 300) }],
  };
}

// ---------------------------------------------------------------- notes

/** Seconds of a round from the start of its freeze time, to a tenth. */
const tenth = (n: number) => Math.round(n * 10) / 10;

/**
 * The Notes mode's left panel: a new note at the moment shown, about players (toggled here
 * or by a click on them on the map), a side and a team, lasting a moment or a while, with
 * links to other matches; and this moment opened as a strat board.
 */
export function NotesPanel({
  demo,
  replay,
  round,
  tick,
  time,
  numbers,
  players,
  onPlayers,
  makeBoard,
  teamNames,
}: {
  demo: DemoDetail;
  replay: Replay;
  round: RoundIndex;
  tick: number;
  /** The replay's time in the round (seconds from its start). */
  time: number;
  numbers: number[];
  /** The players the new note is about: the viewer holds them, a click on the map toggles them. */
  players: number[];
  onPlayers: (p: number[]) => void;
  /** Builds the strat board of this moment. */
  makeBoard: () => CreateStratInput;
  /** The replay's teams 0 and 1 as the match names them. */
  teamNames: [string, string];
}) {
  const { t } = useTranslation();
  const add = useAddComment(demo.id);
  const createStrat = useCreateStrat();
  const [text, setText] = useState("");
  const [about, setAbout] = useState<NoteAbout>(NO_ABOUT);
  // A note that lasts starts where it was when "A while" was chosen: the replay can play on to its end.
  const [lasts, setLasts] = useState<number | null>(null);
  const [anchor, setAnchor] = useState<{ round: number; tick: number } | null>(null);
  const scope: DemoScope | null = demo.match.pro ? "pro" : demo.match.teamId;
  const [error, setError] = useState<string | null>(null);
  const [board, setBoard] = useState<{ id: string } | null>(null);
  const at = anchor ?? { round: round.index, tick };
  const atRound = replay.rounds[at.round];
  const start = (at.tick - atRound.startTick) / replay.tickRate;
  const max = (atRound.postTick - atRound.startTick) / replay.tickRate;
  const here = t("demos.notes.at", { n: atRound.number, clock: clock(start) });
  const fail = (e: unknown) => setError(errorText(t, e));

  const pickPlayers = (next: number[]) => {
    // The first player picked tells the team and the side the note is about, unless chosen.
    if (players.length === 0 && next.length > 0 && about.team === null && about.side === null) {
      const team = replay.players[next[0]]?.team;
      if (team === 0 || team === 1) setAbout((a) => ({ ...a, team, side: team === round.round.tTeam ? "t" : "ct" }));
    }
    onPlayers(next);
  };
  const setLasting = (v: number | null) => {
    setLasts(v);
    setAnchor(v === null ? null : (anchor ?? { round: round.index, tick: Math.round(tick) }));
  };

  const openBoard = async (): Promise<string | null> => {
    try {
      const strat = await createStrat.mutateAsync(makeBoard());
      setBoard({ id: strat.id });
      return strat.id;
    } catch (e) {
      fail(e);
      return null;
    }
  };

  const submit = async (e: FormEvent, withBoard: boolean) => {
    e.preventDefault();
    setError(null);
    if (!text.trim()) return;
    const stratId = withBoard ? await openBoard() : null;
    if (withBoard && !stratId) return;
    const seconds = tenth(start);
    add.mutate(
      {
        round: at.round,
        tick: Math.round(at.tick),
        text: text.trim(),
        stratId,
        players: players.map((i) => ({ index: i, name: replay.players[i]?.name.slice(0, 64) ?? "" })),
        seconds,
        endTick: lasts === null ? null : Math.round(at.tick + lasts * replay.tickRate),
        endSeconds: lasts === null ? null : tenth(seconds + lasts),
        side: about.side,
        team: about.team,
        links: about.links,
      },
      {
        onSuccess: () => {
          setText("");
          setAbout(NO_ABOUT);
          setLasting(null);
          setAnchor(null);
          onPlayers([]);
        },
        onError: fail,
      },
    );
  };

  return (
    <div className="demo-notes">
      <form className="demo-note-form" onSubmit={(e) => void submit(e, false)}>
        <label className="field">
          <span>{t("demos.notes.new", { at: here })}</span>
          <textarea rows={3} maxLength={500} value={text} placeholder={t("demos.notes.placeholder")} onChange={(e) => setText(e.target.value)} />
        </label>
        <PlayerToggles replay={replay} tTeam={round.round.tTeam} numbers={numbers} picked={players} onChange={pickPlayers} />
        <NoteSideTeam about={about} onChange={setAbout} teamNames={teamNames} />
        <LastsField start={start} max={max} now={at.round === round.index ? time : null} value={lasts} onChange={setLasting} />
        <LinksEditor links={about.links} onChange={(links) => setAbout({ ...about, links })} scope={scope} />
        <div className="note-actions">
          <button type="submit" className="btn btn-primary btn-small" disabled={!text.trim() || add.isPending}>
            {t("demos.notes.add")}
          </button>
          <button type="button" className="btn btn-secondary btn-small" disabled={!text.trim() || add.isPending || createStrat.isPending} onClick={(e) => void submit(e as unknown as FormEvent, true)}>
            {t("demos.notes.addWithBoard")}
          </button>
        </div>
        {error && <p className="field-error small">{error}</p>}
      </form>
      <div className="demo-board-row">
        <button type="button" className="btn btn-quiet btn-small" disabled={createStrat.isPending} onClick={() => void openBoard()}>
          <Icon name="map" size={14} />
          {t("demos.notes.board")}
        </button>
        {board && (
          <Link to={`/library/${replay.map}/strats?b=${board.id}`} className="small">
            {t("demos.notes.boardMade")}
          </Link>
        )}
      </div>
    </div>
  );
}

/** Notes filtered in the viewer: by side, team, player, author and words (as on the match page). */
const NO_FILTER: NoteFilter = {};

/**
 * The Notes mode's right panel: this map's notes with their filters (the rules of the match
 * page, `filterNotes`); a click goes to the moment, its author changes or deletes it here.
 */
export function NotesListPanel({
  demo,
  replay,
  round,
  time,
  numbers,
  onGo,
  teamNames,
}: {
  demo: DemoDetail;
  replay: Replay;
  round: RoundIndex;
  time: number;
  numbers: number[];
  onGo: (roundIndex: number, seconds: number) => void;
  teamNames: [string, string];
}) {
  const { t } = useTranslation();
  const comments = useComments(demo.id);
  const del = useDeleteComment(demo.id);
  const edit = useEditComment(demo.id);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [f, setF] = useState<NoteFilter>(NO_FILTER);
  const set = (change: Partial<NoteFilter>) => setF((x) => ({ ...x, ...change }));
  const scope: DemoScope | null = demo.match.pro ? "pro" : demo.match.teamId;
  const all = useMemo(() => comments.data ?? [], [comments.data]);
  const shown = filterNotes(all, f);
  const seconds = (r: number, tk: number) => (tk - replay.rounds[r].startTick) / replay.tickRate;
  const fail = (e: unknown) => setError(errorText(t, e));
  // A note's player is on the side their team plays in the note's round.
  const sideIn = (r: number) => (i: number) => (replay.players[i] ? (replay.players[i].team === replay.rounds[r]?.tTeam ? "t" : "ct") : null);

  const byName = (names: string[]) => [...new Map(names.filter((n) => n.trim()).map((n) => [nameKey(n), n.trim()])).values()].sort((a, b) => a.localeCompare(b));
  const teams = byName([...teamNames, ...all.flatMap((n) => (n.team ? [n.team.name] : []))]);
  const people = byName(all.flatMap((n) => n.players.map((p) => p.name)));
  const authors = [...new Map(all.flatMap((n) => (n.author ? [[n.author.id, n.author] as const] : []))).values()].sort((a, b) => a.name.localeCompare(b.name));
  const any = (label: string) => ({ value: "", label });

  return (
    <div className="demo-notes demo-notes-list">
      <div className="notes-list-head">
        <h2>
          {t("demos.notes.listTitle")}
          <span className="count-badge">{noteFilterSet(f) ? `${shown.length}/${all.length}` : all.length}</span>
        </h2>
        <span className="muted small">{t("demos.notes.thisMap")}</span>
      </div>
      {all.length > 0 && (
        <div className="notes-filters-row" role="group" aria-label={t("notes.filters")}>
          <div className="segmented segmented-small" role="group" aria-label={t("notes.side")}>
            {(["", "t", "ct"] as const).map((s) => (
              <button key={s || "both"} type="button" className="seg" aria-pressed={(f.side ?? "") === s} onClick={() => set({ side: s as NoteSide | "" })}>
                {s ? t(`live.sideShort.${s}`) : t("notes.both")}
              </button>
            ))}
          </div>
          <Select
            label={t("notes.aboutWhichTeam")}
            value={f.team ?? ""}
            placeholder={t("notes.team")}
            groups={[{ options: [any(t("notes.anyTeam")), ...teams.map((n) => ({ value: n, label: n }))] }]}
            onChange={(v) => set({ team: v })}
          />
          {people.length > 0 && (
            <Select
              label={t("notes.aboutPlayer")}
              value={f.player ?? ""}
              placeholder={t("notes.player")}
              groups={[{ options: [any(t("notes.anyPlayer")), ...people.map((n) => ({ value: n, label: n }))] }]}
              onChange={(v) => set({ player: v })}
            />
          )}
          {authors.length > 1 && (
            <Select
              label={t("notes.byAuthor")}
              value={f.by ?? ""}
              placeholder={t("notes.author")}
              groups={[{ options: [any(t("notes.anyAuthor")), ...authors.map((a) => ({ value: a.id, label: a.name }))] }]}
              onChange={(v) => set({ by: v })}
            />
          )}
          <label className="match-search">
            <span className="visually-hidden">{t("notes.search")}</span>
            <Icon name="search" size={14} />
            <input type="search" value={f.q ?? ""} placeholder={t("notes.search")} onChange={(e) => set({ q: e.target.value })} />
          </label>
          {noteFilterSet(f) && (
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setF(NO_FILTER)}>
              {t("notes.clearFilters")}
            </button>
          )}
        </div>
      )}
      {error && <p className="field-error small">{error}</p>}
      {comments.data && all.length === 0 && <p className="muted small">{t("demos.notes.none")}</p>}
      {all.length > 0 && shown.length === 0 && <p className="muted small">{t("notes.noneMatch")}</p>}
      <ol className="demo-note-list">
        {shown.map((c) =>
          editing === c.id ? (
            <li key={c.id} className="is-editing">
              <NoteEditor
                note={c}
                replay={replay}
                numbers={numbers}
                now={c.round === round.index ? time : null}
                teamNames={teamNames}
                scope={scope}
                saving={edit.isPending}
                onCancel={() => setEditing(null)}
                onSave={(change) => edit.mutate({ id: c.id, ...change }, { onSuccess: () => setEditing(null), onError: fail })}
              />
            </li>
          ) : (
            <li key={c.id} className={c.round === round.index ? "is-this-round" : ""}>
              <button type="button" className="demo-note-go" onClick={() => onGo(c.round, Math.max(0, seconds(c.round, c.tick)))}>
                <span className="mono small muted">
                  {t("demos.notes.at", { n: replay.rounds[c.round]?.number ?? c.round + 1, clock: c.seconds !== null ? noteWhen(c) : clock(seconds(c.round, c.tick)) })}
                </span>
                <span className="demo-note-text">{c.text}</span>
                {c.pin && <span className="chip chip-small demo-note-pin">{t(`demos.notes.pinned.${c.pin.kind}`)}</span>}
              </button>
              <NotePlayers players={c.players} numbers={numbers} sideOf={sideIn(c.round)} />
              <NoteTags side={c.side} team={c.team} links={c.links} />
              <div className="demo-note-meta small muted">
                {c.author && <Avatar name={c.author.name} url={c.author.avatarUrl} size={18} />}
                <span>{c.author?.name ?? t("demos.notes.someone")}</span>
                {c.editedAt && <span title={new Date(c.editedAt).toLocaleString()}>{t("notes.edited")}</span>}
                {c.stratId && <Link to={`/library/${replay.map}/strats?b=${c.stratId}`}>{t("demos.notes.openBoard")}</Link>}
                {c.canEdit && (
                  <button type="button" className="link-button" onClick={() => setEditing(c.id)}>
                    {t("notes.edit")}
                  </button>
                )}
                {c.canDelete && (
                  <button type="button" className="link-button" onClick={() => del.mutate(c.id, { onError: fail })}>
                    {t("demos.notes.delete")}
                  </button>
                )}
              </div>
            </li>
          ),
        )}
      </ol>
    </div>
  );
}

interface NoteChange {
  text: string;
  side: NoteAbout["side"];
  team: NoteAbout["team"];
  links: NoteAbout["links"];
  players: { index: number; name: string }[];
  endTick: number | null;
  endSeconds: number | null;
}

/** A note being changed by its author: everything a new note has, but its moment. */
function NoteEditor({
  note,
  replay,
  numbers,
  now,
  teamNames,
  scope,
  saving,
  onSave,
  onCancel,
}: {
  note: DemoComment;
  replay: Replay;
  numbers: number[];
  /** The replay's time when it is in the note's round. */
  now: number | null;
  teamNames: [string, string];
  scope: DemoScope | null;
  saving: boolean;
  onSave: (change: NoteChange) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(note.text);
  const [about, setAbout] = useState<NoteAbout>({ side: note.side, team: note.team?.index ?? null, links: linkInputs(note.links) });
  // Players of the replay only: a name kept from an older replay is dropped if the index is gone.
  const [players, setPlayers] = useState<number[]>(note.players.map((p) => p.index).filter((i) => i < replay.players.length));
  const r = replay.rounds[note.round];
  const start = note.seconds ?? (r ? (note.tick - r.startTick) / replay.tickRate : 0);
  const max = r ? (r.postTick - r.startTick) / replay.tickRate : start + 600;
  const [lasts, setLasts] = useState<number | null>(
    note.endSeconds !== null && note.seconds !== null ? tenth(note.endSeconds - note.seconds) : note.endTick !== null ? tenth((note.endTick - note.tick) / replay.tickRate) : null,
  );
  return (
    <form
      className="demo-note-form demo-note-edit"
      onSubmit={(e) => {
        e.preventDefault();
        if (!text.trim()) return;
        onSave({
          text: text.trim(),
          ...about,
          players: players.map((i) => ({ index: i, name: replay.players[i]?.name.slice(0, 64) ?? "" })),
          endTick: lasts === null ? null : Math.round(note.tick + lasts * replay.tickRate),
          endSeconds: lasts === null ? null : tenth(start + lasts),
        });
      }}
    >
      <label className="field">
        <span>{t("notes.editLabel")}</span>
        <textarea rows={2} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
      </label>
      {r && <PlayerToggles replay={replay} tTeam={r.tTeam} numbers={numbers} picked={players} onChange={setPlayers} />}
      <NoteSideTeam about={about} onChange={setAbout} teamNames={teamNames} />
      <LastsField start={start} max={max} now={now} value={lasts} onChange={setLasts} />
      <LinksEditor links={about.links} onChange={(links) => setAbout({ ...about, links })} scope={scope} />
      <div className="note-actions">
        <button type="submit" className="btn btn-primary btn-small" disabled={!text.trim() || saving}>
          {t("notes.save")}
        </button>
        <button type="button" className="btn btn-quiet btn-small" onClick={onCancel}>
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}
